import { Fragment, createElement, type ReactNode } from 'react'
import { usePage } from '@inertiajs/react'
// Locale definitions are sourced from resources/locales.json (shared with the PHP backend).
// To add or modify locales, edit that file only.
import localesJson from '../../locales.json'

export type Locale = {
  slug: string
  contentfulCode: string
  hreflangCode: string
  language: string
  country: string
  isEU: boolean
}

export const locales: Locale[] = localesJson

export const defaultLocale = locales[0].slug

export const useTranslations = () => {
  const { props, url } = usePage()
  const translations = props.translations as { [key: string]: string }
  return {
    t: (string: string) => {
      if (getLocale(url) === defaultLocale) return string
      if (!translations) return string
      return translations[string] || string
    },
    translations,
  }
}

// Interpolate `{var}` placeholders into the translated template, returning a
// ReactNode array suitable for JSX. Use this to keep full sentences as a single
// translation key (so translators see context) while still embedding dynamic
// values like search queries. Example:
//   {interpolate(t('Discover more about {query} with Ask AI'), {
//     query: <span className="text-primaryBlue">{query}</span>,
//   })}
export const interpolate = (template: string, vars: Record<string, ReactNode>): ReactNode[] => {
  const parts = template.split(/(\{\w+\})/g)
  return parts.map((part, i) => {
    const match = part.match(/^\{(\w+)\}$/)
    if (match && match[1] in vars) {
      return createElement(Fragment, { key: i }, vars[match[1]])
    }
    return part
  })
}

// Create a regex that only matches actual supported locale slugs
const supportedLocalePattern = locales.map((locale) => locale.slug).join('|')
const pathLocaleRegex = new RegExp('^/(' + supportedLocalePattern + ')(/.*)?$')

// based on current path, return locale info
export const useLocale = (pathname?: string) => {
  const currentLocale = getLocale(pathname)
  const currentContentfulCode =
    locales.find((loc) => loc.slug === currentLocale)?.contentfulCode || locales[0].contentfulCode
  return {
    currentLocale,
    currentContentfulCode,
    lang: currentLocale.split('-')[0],
    region: currentLocale.split('-')[1] ?? undefined,
  }
}

export const getLocale = (path: string | undefined = undefined) => {
  if (!path) path = usePage()?.url
  const matches = path?.match(pathLocaleRegex)
  return matches && matches.length === 3 ? matches[1] : defaultLocale
}

// EU + EEA country codes (incl. the UK). A browser locale whose region is in this set is
// treated as European — used both to route European English browsers to en-gb and to
// preselect the bitwarden.eu data region on account-creation forms.
const euEeaRegions = new Set([
  // EU members
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
  // EEA non-EU
  'IS',
  'LI',
  'NO',
  // UK (routes to bitwarden.eu)
  'GB',
])

// True when a browser locale's region code names an EU/EEA country (incl. the UK),
// e.g. 'en-DE', 'pt-PT', 'de_AT'. Returns false when the locale carries no region
// code (a bare 'de' or 'pt' says nothing about the user's country).
export const isBrowserInEuRegion = (browserLocale: string | undefined): boolean => {
  const region = browserLocale?.split(/[-_]/)[1]
  return region ? euEeaRegions.has(region.toUpperCase()) : false
}

// Find the best matching supported locale for a full browser locale string (e.g. 'en-GB', 'de-AT').
// Returns null when no match is found, meaning the user stays on their current locale.
// For English, browsers in an EU/EEA region are routed to en-gb; all others stay on en-us.
// For every other language, an exact slug match is tried first, then a language-code fallback
// (e.g. pt-PT → pt-br, es-ES → es-la).
export const findTargetLocale = (browserLocale: string): Locale | null => {
  const normalized = browserLocale.toLowerCase().replace('_', '-')
  const exact = locales.find((l) => l.slug === normalized)
  if (exact) return exact
  const [langCode] = normalized.split('-')
  if (langCode === 'en') {
    return isBrowserInEuRegion(browserLocale) ? (locales.find((l) => l.slug === 'en-gb') ?? null) : null
  }
  return locales.find((l) => l.slug.startsWith(langCode + '-')) ?? null
}

// Format a (current, target) locale pair for the language-detection banner.
// When the two locales share a language (e.g. en-us / en-gb) the labels
// include the country in parens to disambiguate; otherwise just the language.
export const formatLocaleBannerLabels = (current: Locale, target: Locale): { current: string; target: string } => {
  const ambiguous = current.language === target.language
  return {
    current: ambiguous ? `${current.language} (${current.country})` : current.language,
    target: ambiguous ? `${target.language} (${target.country})` : target.language,
  }
}

export const getBrowserLocaleBestMatch = (fallback = defaultLocale) => {
  if (typeof window === 'undefined') return useLocale().currentLocale
  let { language } = navigator
  if (language.length > 2) language = language.substring(0, 2)
  language = language.toLowerCase()
  const matchedLocale = locales.find((local) => local.slug.substring(0, 2) === language)
  if (matchedLocale) return matchedLocale.slug
  return fallback
}

export const localizedPath = (path: string, locale: string) => {
  let newPath = path

  // sanitize input
  if (!newPath || typeof newPath !== 'string' || newPath.trim().length < 1) return ''

  // check for unnecessary https://bitwarden.com prefix
  if (
    newPath.toLowerCase().includes('www.bitwarden.com') ||
    (newPath.toLowerCase().includes('bitwarden.com') && !newPath.toLowerCase().includes('.bitwarden.com'))
  ) {
    newPath = path.substring(path.toLowerCase().indexOf('bitwarden.com') + 13)
    if (!newPath.startsWith('/')) newPath = `/${newPath}`
  }

  if (
    newPath.toLowerCase().startsWith('https://vault.bitwarden.com/') &&
    (newPath.toLocaleLowerCase().includes('register') ||
      newPath.toLocaleLowerCase().includes('login') ||
      newPath.toLocaleLowerCase().includes('trial'))
  ) {
    return locales.find((loc) => loc.slug === locale)?.isEU
      ? `https://vault.bitwarden.eu/${newPath.substring(28)}`
      : newPath
  }

  // check if this is an absolute path, ex: https://google.com
  if (!newPath.startsWith('/') || newPath.startsWith('//')) return path

  // asset and download file paths only work on the production domain via Fastly proxy — always use full URL
  // Note: /download/ (the page) localizes normally; only /download/?app=... (file proxy) requires this
  if (newPath.startsWith('/assets/') || newPath.startsWith('/download/?app=')) {
    return `https://bitwarden.com${newPath}`
  }

  // make sure the path ends with a trailing slash
  if (!newPath.includes('#') && !newPath.includes('?') && !newPath.endsWith('/') && !newPath.includes('.'))
    newPath += '/'
  else if (newPath.includes('?') && !newPath.includes('/?')) newPath = newPath.replace('?', '/?')
  else if (newPath.includes('#') && !newPath.includes('/#') && !newPath.includes('?'))
    newPath = newPath.replace('#', '/#')

  // check if this is a valid locale slug
  const localeSlug =
    locales.find((loc) => locale === loc.slug)?.slug ||
    locales.find((loc) => locale === loc.contentfulCode)?.slug ||
    locales.find((loc) => locale.localeCompare(loc.slug, defaultLocale, { sensitivity: 'base' }) === 0)?.slug ||
    locales.find((loc) => locale.localeCompare(loc.contentfulCode, defaultLocale, { sensitivity: 'base' }) === 0)
      ?.slug ||
    defaultLocale

  // strip a possible locale prefix
  const matches = newPath.match(pathLocaleRegex)
  if (matches && matches.length === 3) {
    newPath = matches[2] || '/'
  }
  return localeSlug === defaultLocale ? newPath : `/${localeSlug}${newPath}`
}

/**
 * Remove locale prefix from path to get the base path for matching.
 * Examples: /es-la/pricing/ -> /pricing/, /pricing/ -> /pricing/
 */
export const removeLocalePrefix = (path: string): string => {
  // Check if path starts with a valid locale prefix
  const match = path.match(pathLocaleRegex)

  if (match) {
    // Remove the locale prefix but keep the rest of the path
    return path.substring(match[1].length + 1) || '/'
  }

  return path
}
